import React from 'react';
import PropTypes from 'prop-types';
import { connect } from '@gasbuddy/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons/faPen';
import { Button, Header } from '@gasbuddy/react-components';
import moment from 'moment';
import Modal from '../../modal';
import { generateHoursString, getHoursForDisplay, hideTimeIf24HourOpen, transformOpeningHours } from './helpers';
import EditDayOfTheWeek from './edit-day-of-the-week';
import styles from './styles.css';
import Notification from '../../notification';
import StationPropType from '../../../utils/prop-types/station';
import { editStation } from '../../../actions';
import { HOURS_BEING_SAVED_MESSAGE } from '../../../../lib/constants/constants';
import BPUtils from '../../../../lib/bputils';

function EditContent({ station, buttonClassName, willSave, onSaved, dispatchEditStation }) {
  const [regularHours, setRegularHours] = React.useState(transformOpeningHours(station.opening_hours).regularHours);
  const [displayHours, setDisplayHours] = React.useState(null);

  React.useEffect(() => {
    const { opening_hours: openingHours } = station;
    const { regularHours: regHours } = transformOpeningHours(openingHours);
    setRegularHours(regHours);
  }, [station.opening_hours]);

  React.useEffect(() => {
    const { regularHours: newDisplayHours } = getHoursForDisplay(regularHours);
    setDisplayHours(newDisplayHours);
  }, [regularHours]);

  const handleOnChange = (operationHours) => {
    const indexToUpdate = regularHours.findIndex(x => x.day === operationHours.day);
    regularHours[indexToUpdate] = operationHours;
    setRegularHours(regularHours);
  };

  const saveStationHours = async (stationHoursToSave) => {
    try {
      const res = await BPUtils.api.put({
        path: `/stations/${station.id}/information?return_hours=true`,
        data: stationHoursToSave,
      });

      BPUtils.store.set('saved_hours', { savedAt: moment().utc().format(), regularHours: regularHours.map(h => hideTimeIf24HourOpen(h)) });

      const { regularHours: newlySavedHours } = transformOpeningHours(res.opening_hours);
      const { serverReturnedDifferentHours, regularHours: newDisplayHours } = getHoursForDisplay(newlySavedHours);

      const hoursByDay = res.opening_hours?.hours_by_day || {};
      dispatchEditStation({
        opening_hours: {
          opening_hours: stationHoursToSave.business_hours,
          hours_by_day: hoursByDay,
        },
      });

      setRegularHours(newlySavedHours);
      setDisplayHours(newDisplayHours);

      if (serverReturnedDifferentHours) {
        Notification.success(HOURS_BEING_SAVED_MESSAGE);
      } else {
        Notification.success('Station Hours were successfully updated');
      }
      onSaved();
    } catch (err) {
      BPUtils.logError(err);
      Notification.error('There was an error updating station hours. Please contact support.');
      onSaved();
    }
  };

  const handleOnSave = async () => {
    willSave();
    const stationHoursString = generateHoursString(regularHours);
    if (stationHoursString.error) {
      Notification.error(stationHoursString.str);
      onSaved();
    } else {
      await saveStationHours({ business_hours: stationHoursString.str });
    }
  };

  const handleOnDelete = async () => {
    willSave();
    await saveStationHours({ business_hours: '' });
  };

  const handleOnModalClose = () => {
    const { opening_hours: openingHours } = station;
    const { regularHours: regHours } = transformOpeningHours(openingHours);

    setRegularHours(regHours);
  };

  return (
    <Modal
      title="EDIT HOURS"
      size="md"
      onSave={handleOnSave}
      onClose={handleOnModalClose}
      onDelete={handleOnDelete}
      saveButtonText="Save Changes"
      deleteButtonText={station.opening_hours?.opening_hours?.length ? 'Clear Station Hours' : null}
      deleteConfirmationMessage="Are you sure you want to remove hours from this station?"
      button={(
        <Button
          type="button"
          className={buttonClassName}
          link
        >
          <FontAwesomeIcon icon={faPen} />
        </Button>
      )}
    >
      <div className={styles.stationHoursModal}>
        <Header uppercase className={styles.stationHoursTitle} as="h3" size="sm">Regular Hours</Header>
        <div className={styles.hoursEditFormContainer}>
          {(displayHours || []).map(item => (
            <EditDayOfTheWeek
              key={item.day}
              operationHours={item}
              onChange={handleOnChange}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
}

EditContent.propTypes = {
  station: StationPropType,
  buttonClassName: PropTypes.string,
  willSave: PropTypes.func,
  onSaved: PropTypes.func,
  dispatchEditStation: PropTypes.func,
};

EditContent.defaultProps = {
  station: null,
  buttonClassName: '',
  willSave: null,
  onSaved: null,
  dispatchEditStation: null,
};

const mapStateToProps = state => ({
  station: state.station,
});

const mapDispatchToProps = dispatch => ({
  dispatchEditStation: stationEdit => dispatch(editStation(stationEdit)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditContent);
